import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Autocomplete,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import PaymentIcon from "@mui/icons-material/Payment";
import ComputerIcon from "@mui/icons-material/Computer";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SaveIcon from "@mui/icons-material/Save";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CustomAlert from "../../Common/Components/CustomAlert";

function TabRestaurant({ emplacement, onSave }) {
  const { t } = useTranslation(["Location"]);
  const userState = useSelector((state) => state.user);

  // Form state with correct field names matching the backend
  const [formData, setFormData] = useState({
    nbSiege: "",
    superficie: "",
    ipAddress: "",
    prixLivraison: "",
    prixLivraisonParPalette: "",
    idTypePaiement: null,
    nomTypePaiementFr: "",
    location: "",
  });

  // Alert state
  const [alert, setAlert] = useState({
    show: false,
    type: "",
    message: "",
  });

  // Payment types state
  const [typePaiements, setTypePaiements] = useState([]);

  useEffect(() => {
    getTypePaiement();
  }, []);

  // Update form with emplacement data
  useEffect(() => {
    if (emplacement) {
      setFormData({
        nbSiege: emplacement.nbSiege || "",
        superficie: emplacement.superficie || "",
        ipAddress: emplacement.ipAddress || "",
        prixLivraison: emplacement.prixLivraison || 0,
        prixLivraisonParPalette: emplacement.prixLivraisonParPalette || 0,
        idTypePaiement: emplacement.idTypePaiement || null,
        nomTypePaiementFr: emplacement.nomTypePaiementFr || "",
        location: emplacement.location || "",
      });
    }
  }, [emplacement]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;

    // Handle numeric fields
    if (field === "prixLivraison" || field === "prixLivraisonParPalette") {
      setFormData((prev) => ({
        ...prev,
        [field]: value, // Don't convert to number immediately to allow typing
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleDateChange = (field) => (date) => {
    setFormData((prev) => ({
      ...prev,
      [field]: date,
    }));
  };

  const handlePaymentTypeChange = (event) => {
    const selectedType = typePaiements.find(
      (t) => t.noTypePaiement === event.target.value,
    );
    setFormData((prev) => ({
      ...prev,
      idTypePaiement: selectedType?.noTypePaiement ?? null,
      nomTypePaiementFr: selectedType.nomFr,
    }));
  };

  const getTypePaiement = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/location/GetTypePaiement`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );
      const data = await response.json();
      setTypePaiements(data);
    } catch (error) {
      setAlert({
        show: true,
        type: "error",
        message: "Error loading payment types",
      });
    }
  };

  const handleSave = async () => {
    console.log("Form data before save:", {
      nbSiege: formData.nbSiege,
      type: typeof formData.nbSiege,
    }); // Detailed debug log

    // Create saveData with direct assignment
    const saveData = {
      // Required fields
      noEmplacement: emplacement?.noEmplacement,
      noAdresse: emplacement?.noAdresse,
      activeTab: 1,

      // Restaurant tab specific data
      nbSiege: Number(formData.nbSiege), // Force number conversion
      superficie: Number(formData.superficie), // Force number conversion
      IpAddress: formData.ipAddress,
      PrixLivraison: parseFloat(formData.prixLivraison) || 0, // Handle float values
      PrixLivraisonParPalette:
        parseFloat(formData.prixLivraisonParPalette) || 0, // Handle float values
      IdTypePaiement: formData.idTypePaiement,
      NomTypePaiementFr: formData.nomTypePaiementFr,
      // DataAnywhere
      location: formData.location || null,
    };

    // Debug log to verify the conversion
    console.log("Save data nbSiege:", {
      value: saveData.nbSiege,
      type: typeof saveData.nbSiege,
    });

    const result = await onSave(saveData, userState.token, 1);

    setAlert({
      show: true,
      type: result.success ? "success" : "error",
      message: result.message,
    });
  };

  return (
    <Paper
      elevation={4}
      sx={{
        mx: "5%",
        mt: 3,
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg,#1976d2,#42a5f5)",
          color: "white",
          p: 3,
        }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          display="flex"
          alignItems="center"
          gap={1}
        >
          <RestaurantIcon />
          {t("RestaurantInformation")}
        </Typography>
      </Box>

      <Box sx={{ p: 4 }}>
        {/* General Information */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              fontWeight="bold"
              display="flex"
              alignItems="center"
              gap={1}
            >
              <RestaurantIcon />
              {t("GeneralInformation")}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("seatsNumber")}
                  value={formData.nbSiege}
                  onChange={handleInputChange("nbSiege")}
                  type="number"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("Surface")}
                  value={formData.superficie}
                  onChange={handleInputChange("superficie")}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Payment */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              fontWeight="bold"
              display="flex"
              gap={1}
              alignItems="center"
            >
              <PaymentIcon />
              {t("Payment")}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  value={
                    typePaiements.find(
                      (type) => type.noTypePaiement === formData.idTypePaiement,
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      idTypePaiement: newValue ? newValue.noTypePaiement : null,
                      nomTypePaiementFr: newValue ? newValue.nomFr : "",
                    }));
                  }}
                  options={typePaiements}
                  getOptionLabel={(option) =>
                    userState.lang === "EN" ? option.nomEn : option.nomFr
                  }
                  renderInput={(params) => (
                    <TextField {...params} label={t("PaymentType")} />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* System */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              fontWeight="bold"
              display="flex"
              gap={1}
              alignItems="center"
            >
              <ComputerIcon />
              {t("SystemConfiguration")}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("IPCaisse")}
                  value={formData.ipAddress}
                  onChange={handleInputChange("ipAddress")}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="DataAnywhere LocationId"
                  value={formData.location}
                  onChange={handleInputChange("location")}
                  placeholder="Optional"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Delivery */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              fontWeight="bold"
              display="flex"
              gap={1}
              alignItems="center"
            >
              <LocalShippingIcon />
              {t("Delivery")}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("DeliveryCost")}
                  value={formData.prixLivraison}
                  onChange={handleInputChange("prixLivraison")}
                  type="number"
                  inputProps={{
                    step: "0.01",
                    min: "0",
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("DeliveryCostByPallets")}
                  value={formData.prixLivraisonParPalette}
                  onChange={handleInputChange("prixLivraisonParPalette")}
                  type="number"
                  inputProps={{
                    step: "0.01",
                    min: "0",
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          sx={{
            mt: 2,
            px: 5,
            height: 45,
            borderRadius: 3,
            fontWeight: "bold",
            textTransform: "none",
          }}
        >
          {t("Save")}
        </Button>
      </Box>

      <CustomAlert
        open={alert.show}
        closeMessage={() =>
          setAlert((prev) => ({
            ...prev,
            show: false,
          }))
        }
        typeMessage={alert.type}
        message={alert.message}
      />
    </Paper>
  );
}

export default TabRestaurant;
